import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { verifyOAuthState, getStoredProviderId } from "@/lib/auth/oauthHandler";
import { checkUserRole } from "@/lib/userUtils";

/**
 * Normalize origin by removing 'www.' prefix to ensure consistency
 * with OAuth provider configurations
 */
function normalizeOrigin(origin: string): string {
  try {
    const url = new URL(origin);
    // Remove 'www.' from hostname if present
    if (url.hostname.startsWith('www.')) {
      url.hostname = url.hostname.substring(4);
    }
    return url.origin;
  } catch {
    // Fallback if URL parsing fails
    return origin.replace(/^https?:\/\/www\./, (match) => match.replace('www.', ''));
  }
}

export const AuthCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Check if this is a password reset callback
        // Supabase sends password reset tokens in URL hash, not query params
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const type = hashParams.get('type') || searchParams.get('type');
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        
        if (type === 'recovery') {
          // This is a password reset callback
          // Supabase automatically creates a session when user clicks the reset link
          // We need to redirect to the reset password page
          if (accessToken) {
            // Set the session first
            const { error: sessionError } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken || '',
            });
            
            if (sessionError) {
              // Error setting reset session - continue anyway
            }
            
            // Redirect to reset password page
            navigate('/auth/reset-password', { replace: true });
            return;
          }
          
          // Fallback: check query params
          const token = searchParams.get('token');
          if (token) {
            navigate(`/auth/reset-password?token=${token}&type=${type}`, { replace: true });
            return;
          }
        }

        // Extract OAuth parameters from URL
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        // Handle OAuth errors from provider
        if (error) {
          throw new Error(errorDescription || error || 'OAuth authorization failed');
        }

        if (!code) {
          throw new Error('No authorization code received from OAuth provider');
        }

        if (!state) {
          throw new Error('No state parameter received. This may indicate a security issue.');
        }

        // Get provider ID from stored state or try to infer from URL
        const providerId = getStoredProviderId() || 'google'; // Fallback to google for now

        // Verify state to prevent CSRF attacks
        // Note: In development, we're more lenient with state verification
        const stateValid = verifyOAuthState(providerId, state);
        if (!stateValid && !import.meta.env.DEV) {
          throw new Error('Invalid state parameter. This may indicate a security issue.');
        } else if (!stateValid) {
          // State verification failed, but continuing in development mode
        }

        // Call edge function to exchange code for tokens and create session
        // Note: We use supabase.functions.invoke which handles auth and errors better
        // But since user isn't authenticated yet, we'll use direct fetch with apikey
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

        if (!supabaseUrl || !supabaseKey) {
          throw new Error('Supabase configuration is missing. Please check your environment variables.');
        }

        const response = await fetch(
          `${supabaseUrl}/functions/v1/exchange-oauth-token`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'apikey': supabaseKey,
              'Authorization': `Bearer ${supabaseKey}`, // Some edge functions require this
            },
            body: JSON.stringify({
              provider: providerId,
              code,
              redirectUri: `${normalizeOrigin(window.location.origin)}/auth/callback`,
            }),
          }
        );

        if (!response.ok) {
          let errorMessage = `Token exchange failed: ${response.status}`;
          try {
            const errorData = await response.json();
            errorMessage = errorData.error || errorData.message || errorMessage;
          } catch {
            // If response isn't JSON, use status text
            errorMessage = `Token exchange failed: ${response.status} ${response.statusText}`;
          }
          throw new Error(errorMessage);
        }

        const responseData = await response.json();
        const { session: newSession, user, userInfo, needsManualSignIn, id_token: idToken, provider: idTokenProvider } = responseData;

        if (!user || !user.id) {
          throw new Error('Failed to create user');
        }

        // Existing user with id_token: sign in via Supabase (keeps their password intact)
        if (idToken && idTokenProvider) {
          const { data: idTokenData, error: idTokenError } = await supabase.auth.signInWithIdToken({
            provider: idTokenProvider,
            token: idToken,
          });
          if (idTokenError) {
            throw new Error(idTokenError.message || 'Failed to sign in with Google. Try signing in with your email and password.');
          }
          if (idTokenData?.session?.user?.id !== user.id) {
            throw new Error('Session user does not match. Please try again.');
          }
          await redirectUser(user.id);
          return;
        }

        // Set session if we have one (new users get session from edge function)
        if (newSession && newSession.access_token) {
          // Ensure we have both tokens
          if (!newSession.refresh_token) {
            throw new Error('Session response is missing refresh token');
          }

          // Try to set the session
          // Note: setSession may not return session in response, so we verify with getSession after
          const { error: setSessionError } = await supabase.auth.setSession({
            access_token: newSession.access_token,
            refresh_token: newSession.refresh_token,
          });

          if (setSessionError) {
            throw new Error(`Failed to set session: ${setSessionError.message}`);
          }

          // Verify session was set by getting it (setSession doesn't always return session in response)
          const { data: { session: verifySession }, error: getSessionError } = await supabase.auth.getSession();
          
          if (getSessionError) {
            throw new Error(`Failed to verify session: ${getSessionError.message}`);
          }

          if (!verifySession || verifySession.user.id !== user.id) {
            throw new Error('Session was not properly set after OAuth callback');
          }
          
          // Wait for auth state change event to ensure session is fully propagated
          // This prevents race conditions where AuthGuard checks before session is available
          await new Promise<void>((resolve, reject) => {
            let subscription: { unsubscribe: () => void } | null = null;
            
            const timeout = setTimeout(() => {
              if (subscription) {
                subscription.unsubscribe();
              }
              // If timeout, verify session is still available
              supabase.auth.getSession().then(({ data: { session } }) => {
                if (session?.user?.id === user.id) {
                  resolve();
                } else {
                  reject(new Error('Timeout waiting for auth state change and session not available'));
                }
              });
            }, 3000);

            const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange(
              (event, session) => {
                if (event === 'SIGNED_IN' && session?.user?.id === user.id) {
                  clearTimeout(timeout);
                  if (subscription) {
                    subscription.unsubscribe();
                  }
                  resolve();
                } else if (event === 'SIGNED_OUT' || (event === 'TOKEN_REFRESHED' && !session)) {
                  clearTimeout(timeout);
                  if (subscription) {
                    subscription.unsubscribe();
                  }
                  reject(new Error('Session was lost during setup'));
                }
              }
            );
            
            subscription = authSubscription;
          });

          // Final verification before redirect
          const { data: { session: finalSession } } = await supabase.auth.getSession();
          if (!finalSession || finalSession.user.id !== user.id) {
            throw new Error('Session verification failed before redirect');
          }
        } else if (needsManualSignIn) {
          // Existing user but we couldn't create a session (e.g. no id_token). Don't overwrite their password.
          throw new Error('Your account was recognized but we couldn’t sign you in with Google this time. Please sign in with your email and password.');
        } else {
          throw new Error('No session provided and manual sign-in not available');
        }

        // Check onboarding status and redirect
        await redirectUser(user.id);
      } catch (error) {
        setStatus('error');
        
        let errorMsg = 'An error occurred during authentication';
        if (error instanceof Error) {
          errorMsg = error.message;
          
          // Provide helpful error messages for common issues
          if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
            errorMsg = 'Unable to connect to the server. Please check:\n' +
              '1. The edge function "exchange-oauth-token" is deployed\n' +
              '2. Your internet connection is working\n' +
              '3. Supabase environment variables are configured correctly';
          } else if (error.message.includes('Token exchange failed')) {
            errorMsg = 'OAuth token exchange failed. Please try again or contact support if the issue persists.';
          }
        }
        
        setErrorMessage(errorMsg);
      }
    };

    handleCallback();
  }, [navigate, searchParams]);

  const redirectUser = async (userId: string) => {
    try {
      // Check if user is global admin
      const isGlobalAdmin = await checkUserRole('global_admin');

      if (isGlobalAdmin) {
        navigate('/dashboard', { replace: true });
        return;
      }

      // Check if user has completed onboarding
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('user_id', userId)
        .single();

      if (profileError && profileError.code !== 'PGRST116') {
        // PGRST116 is "not found" - that's okay for new users
        // Silently handle error
      }

      if (profile?.company_id) {
        navigate('/dashboard', { replace: true });
      } else {
        navigate('/onboarding', { replace: true });
      }
    } catch (error) {
      // Default to onboarding if there's an error
      navigate('/onboarding', { replace: true });
    }
  };

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone p-4">
        <div className="w-full max-w-md space-y-4 text-center">
          <div className="text-destructive text-lg font-semibold">
            Authentication Error
          </div>
          <p className="text-muted-foreground">{errorMessage}</p>
          <button
            onClick={() => navigate('/auth')}
            className="text-primary hover:underline"
          >
            Return to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone p-4">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Completing sign in...</p>
      </div>
    </div>
  );
};
